import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { ThemeService } from '../../core/core';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TagDialogComponent, TagFormData } from './tag-dialog';

// Shared components
import {
  SearchFormComponent,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';

// Types
interface TagItem {
  id: number;
  labelEncoding: string;
  type: string;
  describe: string;
  isBuiltIn: boolean;
  isInheritable: boolean;
  isPageVisible: boolean;
  canOperate: boolean;
}

interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-tag-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TagModule,
    SearchFormComponent,
    DataTableComponent,
  ],
  templateUrl: './tag-management.html',
  styleUrl: './tag-management.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagManagement implements OnDestroy {
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  protected readonly themeService = inject(ThemeService);

  private dialogRef: DynamicDialogRef | null = null;

  // Pagination signals
  currentPage = signal(0);
  itemsPerPage = signal(20);
  totalItems = signal(0);
  loading = signal(false);

  // Type options for dropdown
  typeOptions: DropdownOption[] = [
    { label: 'API Tags', value: 'API Tags' },
    { label: 'Role Tags', value: 'Role Tags' },
    { label: 'Menu Label', value: 'Menu Label' },
  ];

  // Extended mock data for comprehensive table display
  tags = signal<TagItem[]>([
    {
      id: 1,
      labelEncoding: 'SYS_ADMIN',
      type: 'Role Tags',
      describe: 'System administrator with full access privileges',
      isBuiltIn: true,
      isInheritable: false,
      isPageVisible: false,
      canOperate: false,
    },
    {
      id: 2,
      labelEncoding: 'USER_MGMT',
      type: 'API Tags',
      describe: 'User management API endpoints',
      isBuiltIn: true,
      isInheritable: false,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 3,
      labelEncoding: 'TENANT_ADMIN',
      type: 'Role Tags',
      describe: 'Tenant administrator with limited scope',
      isBuiltIn: true,
      isInheritable: true,
      isPageVisible: false,
      canOperate: false,
    },
    {
      id: 4,
      labelEncoding: 'ORDER_MENU',
      type: 'Menu Label',
      describe: 'Order management menu navigation',
      isBuiltIn: false,
      isInheritable: true,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 5,
      labelEncoding: 'PRODUCT_API',
      type: 'API Tags',
      describe: 'Product catalog and inventory APIs',
      isBuiltIn: true,
      isInheritable: true,
      isPageVisible: false,
      canOperate: true,
    },
    {
      id: 6,
      labelEncoding: 'CUSTOMER_SUPPORT',
      type: 'Role Tags',
      describe: 'Customer support representative role',
      isBuiltIn: false,
      isInheritable: false,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 7,
      labelEncoding: 'INVENTORY_MENU',
      type: 'Menu Label',
      describe: 'Inventory management navigation menu',
      isBuiltIn: false,
      isInheritable: true,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 8,
      labelEncoding: 'PAYMENT_API',
      type: 'API Tags',
      describe: 'Payment processing and transaction APIs',
      isBuiltIn: true,
      isInheritable: false,
      isPageVisible: false,
      canOperate: true,
    },
    {
      id: 9,
      labelEncoding: 'WAREHOUSE_MANAGER',
      type: 'Role Tags',
      describe: 'Warehouse operations manager',
      isBuiltIn: false,
      isInheritable: false,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 10,
      labelEncoding: 'ANALYTICS_MENU',
      type: 'Menu Label',
      describe: 'Business analytics and reporting menu',
      isBuiltIn: false,
      isInheritable: false,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 11,
      labelEncoding: 'SHIPPING_API',
      type: 'API Tags',
      describe: 'Shipping and logistics API endpoints',
      isBuiltIn: true,
      isInheritable: true,
      isPageVisible: false,
      canOperate: true,
    },
    {
      id: 12,
      labelEncoding: 'SALES_REP',
      type: 'Role Tags',
      describe: 'Sales representative with order access',
      isBuiltIn: false,
      isInheritable: false,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 13,
      labelEncoding: 'REPORTS_MENU',
      type: 'Menu Label',
      describe: 'Financial and operational reports menu',
      isBuiltIn: false,
      isInheritable: true,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 14,
      labelEncoding: 'NOTIFICATION_API',
      type: 'API Tags',
      describe: 'System notification and messaging APIs',
      isBuiltIn: true,
      isInheritable: false,
      isPageVisible: false,
      canOperate: true,
    },
    {
      id: 15,
      labelEncoding: 'FINANCE_MANAGER',
      type: 'Role Tags',
      describe: 'Financial operations manager',
      isBuiltIn: false,
      isInheritable: false,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 16,
      labelEncoding: 'SETTINGS_MENU',
      type: 'Menu Label',
      describe: 'System configuration and settings menu',
      isBuiltIn: true,
      isInheritable: true,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 17,
      labelEncoding: 'AUDIT_API',
      type: 'API Tags',
      describe: 'Audit trail and compliance APIs',
      isBuiltIn: true,
      isInheritable: false,
      isPageVisible: false,
      canOperate: false,
    },
    {
      id: 18,
      labelEncoding: 'GUEST_USER',
      type: 'Role Tags',
      describe: 'Limited access guest user role',
      isBuiltIn: true,
      isInheritable: false,
      isPageVisible: false,
      canOperate: false,
    },
    {
      id: 19,
      labelEncoding: 'DASHBOARD_MENU',
      type: 'Menu Label',
      describe: 'Main dashboard navigation menu',
      isBuiltIn: true,
      isInheritable: true,
      isPageVisible: true,
      canOperate: true,
    },
    {
      id: 20,
      labelEncoding: 'INTEGRATION_API',
      type: 'API Tags',
      describe: 'Third-party integration APIs',
      isBuiltIn: false,
      isInheritable: true,
      isPageVisible: false,
      canOperate: true,
    },
  ]);

  // Filtered tags based on search criteria
  filteredTags = signal<TagItem[]>([]);

  // Search form configuration
  searchFields: FormFieldConfig[] = [
    {
      key: 'labelEncoding',
      label: 'Label Encoding',
      type: 'text',
      placeholder: 'Please enter',
    },
    {
      key: 'type',
      label: 'Type',
      type: 'select',
      options: this.typeOptions,
      placeholder: 'Please select',
    },
  ];

  // Table columns configuration
  tableColumns: TableColumn[] = [
    {
      prop: 'labelEncoding',
      label: 'Label Encoding',
      width: 150,
    },
    {
      prop: 'type',
      label: 'Type',
      width: 120,
    },
    {
      prop: 'describe',
      label: 'Description',
      width: 300,
    },
    {
      prop: 'isBuiltIn',
      label: 'Built-in tags',
      width: 140,
      type: 'status',
    },
    {
      prop: 'isInheritable',
      label: 'Inheritable',
      width: 140,
      type: 'status',
    },
    {
      prop: 'isPageVisible',
      label: 'Page visible',
      width: 140,
      type: 'status',
    },
    {
      prop: 'canOperate',
      label: 'Operate',
      width: 140,
      type: 'actions',
    },
  ];

  constructor() {
    this.updateFilteredTags();
  }

  onSearch(searchData: any): void {
    this.updateFilteredTags(searchData);
  }

  onReset(): void {
    this.updateFilteredTags({});
  }

  onNew(): void {
    this.dialogRef = this.dialogService.open(TagDialogComponent, {
      header: 'New Tag',
      width: '480px',
      modal: true,
      closable: true,
      data: {},
    });

    this.dialogRef.onClose.subscribe((result: TagFormData) => {
      if (result) {
        // Create new tag
        const newTag: TagItem = {
          id: this.tags().length + 1,
          labelEncoding: result.labelEncoding,
          type: result.type,
          describe: result.description,
          isBuiltIn: false,
          isInheritable: result.isInheritable,
          isPageVisible: result.isPageVisible,
          canOperate: true,
        };

        // Add to tags array
        this.tags.update((tags) => [...tags, newTag]);
        this.totalItems.set(this.tags().length);
        this.updateFilteredTags();

        console.log('New tag created:', newTag);
      }
    });
  }

  onEdit(tag: TagItem): void {
    this.dialogRef = this.dialogService.open(TagDialogComponent, {
      header: 'Edit Tag',
      width: '480px',
      modal: true,
      closable: true,
      data: { tag },
    });

    this.dialogRef.onClose.subscribe((result: TagFormData) => {
      if (result) {
        // Update existing tag
        this.tags.update((tags) =>
          tags.map((t) =>
            t.id === tag.id
              ? {
                  ...t,
                  labelEncoding: result.labelEncoding,
                  type: result.type,
                  describe: result.description,
                  isInheritable: result.isInheritable,
                  isPageVisible: result.isPageVisible,
                }
              : t
          )
        );

        this.updateFilteredTags();
        console.log('Tag updated:', result);
      }
    });
  }

  onPageChange(event: any): void {
    this.currentPage.set(event.page);
  }

  getTagSeverity(value: boolean): 'info' | 'secondary' {
    return value ? 'info' : 'secondary';
  }

  getTagValue(value: boolean): string {
    return value ? 'yes' : 'no';
  }

  ngOnDestroy(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private updateFilteredTags(searchData: any = {}): void {
    let filtered = this.tags();

    // Apply search filters
    if (searchData.labelEncoding) {
      filtered = filtered.filter(
        (tag) =>
          tag.labelEncoding
            .toLowerCase()
            .includes(searchData.labelEncoding.toLowerCase()) ||
          tag.describe
            .toLowerCase()
            .includes(searchData.labelEncoding.toLowerCase())
      );
    }

    if (searchData.type) {
      filtered = filtered.filter((tag) => tag.type === searchData.type);
    }

    this.filteredTags.set(filtered);
    this.totalItems.set(filtered.length);
    this.currentPage.set(0); // Reset to first page when filtering
  }
}
